/* eslint-disable max-statements */
import { useEffect, useMemo, useState } from 'react';
import { Box, Icon, Link, Text } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { useSelector } from 'react-redux';
import { ModalConfirmationBaseNew } from 'App/components';
import ModalAside from 'App/components/lab/ModalAside/ModalAside';
import {
  CancelAndSaveButtons,
  HeaderContent,
  SortableDataList,
} from 'commons/components';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import { useBoolean } from 'commons/hooks';
import { ProductUnitsCountFilterValue } from 'domains/Orders/components/ProductUnitsCountFilter';
import { useProductUnitsCountFilter } from 'domains/Orders/components/ProductUnitsCountFilter/useProductUnitsCountFilter';
import {
  getOrdersAggregations,
  SavedSearchInterface,
} from 'domains/Orders/Orders';
import {
  defaultFilterParams,
  FiltersParamsType,
  FiltersType,
  defaultFilters,
} from 'domains/Orders/Orders/ordersService';
import FilterModal from 'domains/Orders/Orders/pages/OrderListPage/components/OrderListFilter/FilterModal';
import { useFilterSelectedProducts } from 'domains/Orders/Orders/pages/OrderListPage/hooks/useFilterSelectedProducts';
import {
  trackingConfigSavedSearches,
  trackingCreateSavedSearch,
  trackingUpdateSavedSearch,
} from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { SavedSearchItem } from './SavedSearchItem';
import { useSavedSearches } from '../../useSavedSearches';
import './SavedSearchesConfigModal.scss';

interface SavedSearchesConfigModalProps {
  open: boolean;
  onConfirmSavedSearch: (filters: FiltersParamsType) => void;
  onDismiss: () => void;
  onCouponOptionsLabel: (options: OptionType[]) => void;
}

export function SavedSearchesConfigModal({
  open,
  onDismiss,
  onCouponOptionsLabel,
  onConfirmSavedSearch,
}: Readonly<SavedSearchesConfigModalProps>) {
  const t = useTranslationOrders();
  const [closingConfig, setClosingConfig] = useState(false);
  const {
    savedSearches,
    configureSavedSearches,
    createSavedSearch,
    updateSavedSearch,
  } = useSavedSearches();
  const [configSavedSearches, setConfigSavedSearches] = useState([
    ...savedSearches,
  ]);
  const hasConfigChanges = useMemo(() => {
    if (configSavedSearches.length !== savedSearches.length) return true;
    return configSavedSearches.some((configSS, cIdx) => {
      const originalSS = savedSearches.find((ss) => ss.id === configSS.id);
      return (
        savedSearches.at(cIdx)?.id !== configSS.id ||
        originalSS?.hidden !== configSS.hidden ||
        (originalSS?.deleted ?? false) !== (configSS.deleted ?? false)
      );
    });
  }, [configSavedSearches, savedSearches]);

  const aggregations = useSelector(getOrdersAggregations);

  const [creationFilters, setCreationFilters] = useState(defaultFilterParams);
  const [savedSearchToEdit, setSavedSearchToEdit] =
    useState<SavedSearchInterface | null>(null);
  const savedSearchToEditFilters = useMemo<FiltersType | null>(
    () =>
      savedSearchToEdit
        ? {
            ...defaultFilters,
            ...savedSearchToEdit.filters,
          }
        : null,
    [savedSearchToEdit],
  );
  const savedSearchesToEditProducts = useMemo<string | null>(
    () => savedSearchToEdit?.filters['products'] ?? null,
    [savedSearchToEdit],
  );

  const { selectedProducts, setSelectedProducts, serializeProducts } =
    useFilterSelectedProducts(
      savedSearchesToEditProducts ?? creationFilters.products,
      aggregations,
    );

  const { getProductsCountFilterOption } = useProductUnitsCountFilter();
  const [productUnitsCountFilterValue, setProductUnitsCountFilterValue] =
    useState<ProductUnitsCountFilterValue>({
      selectedOption: getProductsCountFilterOption(
        creationFilters.minUnits,
        creationFilters.maxUnits,
      ),
      minUnits: creationFilters.minUnits,
      maxUnits: creationFilters.maxUnits,
    });

  const [showSavedSearchModal, openSavedSearchModal, closeSavedSearchModal] =
    useBoolean(false);

  useEffect(() => {
    setConfigSavedSearches([...savedSearches]);
  }, [savedSearches]);

  const handleSavedSearchModalDismiss = () => {
    if (savedSearchToEdit) {
      setSelectedProducts([]); // Avoid keeping product filters in creation modal
    }
    setSavedSearchToEdit(null);
    closeSavedSearchModal();
  };

  const handleSavedSearchModalClose = () => {
    setSavedSearchToEdit(null); // We always reset the unconfirmed saved search changes when closing the modal
    setSelectedProducts([]); // Clear selected products from the modal
    closeSavedSearchModal();
  };

  const handleDismiss = () => {
    if (hasConfigChanges) {
      setClosingConfig(true);
    } else {
      onDismiss();
    }
  };

  const handleSavedSearchEdit = (savedSearch: SavedSearchInterface) => {
    setSavedSearchToEdit(savedSearch);
    setSelectedProducts([]); // Clear selected products from the modal if any remain from a pending creation case
    openSavedSearchModal();
  };

  const handleDismissConfirm = () => {
    setClosingConfig(false);
    setConfigSavedSearches([...savedSearches]);
    onDismiss();
  };

  const handleDismissCancel = () => {
    setClosingConfig(false);
  };

  const handleCreateSavedSearchClick = () => {
    openSavedSearchModal();
  };

  const handleChange = (savedSearches: SavedSearchInterface[]) => {
    setConfigSavedSearches(savedSearches);
  };

  const handleConfirm = () => {
    trackingConfigSavedSearches();
    configureSavedSearches(configSavedSearches);
    onDismiss();
  };

  const handleSavedSearchChange = (savedSearch: SavedSearchInterface) => {
    setConfigSavedSearches((prevState) =>
      prevState.map((ss) => (ss.id === savedSearch.id ? savedSearch : ss)),
    );
  };

  const handleConfirmSavedSearch = (
    name: string,
    newFilters: FiltersParamsType,
  ) => {
    setCreationFilters({ ...defaultFilterParams }); // Clear filters from the modal
    setSavedSearchToEdit(null); // Clear any saved search being edited
    setSelectedProducts([]); // Clear selected products from the modal
    // If we are editing an existing saved search, we update it instead of creating a new one
    if (savedSearchToEdit) {
      trackingUpdateSavedSearch();
      updateSavedSearch(savedSearchToEdit.id, name.trim(), newFilters);
    } else {
      trackingCreateSavedSearch();
      createSavedSearch(name.trim(), newFilters);
    }
    onConfirmSavedSearch(newFilters);
  };

  return (
    <>
      <ModalAside
        hasVirtualizationContent
        isOpen={open && !closingConfig}
        onDidDismiss={handleDismiss}
        headerContent={
          <HeaderContent title={t('savedSearches.configModal.title')} />
        }
        footer={
          <Box display="flex" flex="1" width="100%" justifyContent="flex-end">
            <CancelAndSaveButtons
              onCancel={handleDismiss}
              onSave={handleConfirm}
              isDisabled={!hasConfigChanges}
            />
          </Box>
        }
        footerHeight="70px"
      >
        <Box display="flex" gap="4" flexDirection="column">
          <Link textDecoration="none" onClick={handleCreateSavedSearchClick}>
            <Icon color="primary-interactive" source={<PlusCircleIcon />} />
            <Text color="primary-interactive" fontSize="base">
              {t('savedSearches.configModal.create')}
            </Text>
          </Link>
          <div className="stratus--saved-searches-line-item">
            <SortableDataList<SavedSearchInterface>
              items={configSavedSearches}
              onChange={handleChange}
              renderItem={(ss) => (
                <SavedSearchItem
                  savedSearch={ss}
                  onSavedSearchChange={handleSavedSearchChange}
                  onSavedSearchEdit={handleSavedSearchEdit}
                />
              )}
            />
          </div>
        </Box>
      </ModalAside>

      <FilterModal
        show={showSavedSearchModal}
        hideModal={handleSavedSearchModalDismiss}
        filter={savedSearchToEditFilters ?? creationFilters}
        addParam={() => ({})} // Not needed in this context
        selectedProducts={selectedProducts}
        addSelectedProductParam={setSelectedProducts}
        serializeProducts={serializeProducts}
        productUnitsCountFilterValue={productUnitsCountFilterValue}
        addProductUnitsCountFilterParam={setProductUnitsCountFilterValue}
        onCouponOptionsLabel={onCouponOptionsLabel}
        onConfirmSavedSearch={handleConfirmSavedSearch}
        onCancel={handleSavedSearchModalClose}
        savedSearchModalMode={savedSearchToEdit ? 'edit' : 'create'}
        savedSearchName={savedSearchToEdit?.name}
      />

      <ModalConfirmationBaseNew
        show={closingConfig}
        onConfirm={handleDismissConfirm}
        onCancel={handleDismissCancel}
      />
    </>
  );
}
